<!--
  CollectorLineNotes — rule G in one place (collector-clarity, handoff spec
  Sections 3 and 6.2): her comment and the leader's notes, each in its own
  full-width block, on every card shape.

    her comment   lilac block, «العميلة كاتبة», her words WHOLE with her colour
                  word in bold.
    leader note   indigo-framed block, «تعليمات المقر», with the leader's
                  photos first, tagged «المقر». A photo with no note still
                  draws the block.

  NEVER clamp and never ellipsise either text: a long comment wraps and the
  card grows. Other messages on the line (a rejection, a question, a report)
  stack with these blocks; nothing replaces one.

  `photo="beside"` is the grouped member row: her photo, 76 px with the ring,
  on the start side of her comment (alone when she wrote nothing). The other
  shapes draw her photo themselves and leave it `none`.

  `size="sm"` is the marked line: 13 px, the label inline («العميلة كاتبة: …»).

  Draws nothing when the line carries none of these. Spans throughout, so it
  can sit inside a card's open target (a <button>). Read from
  collector/lineView.js `lineInstructions`; her words are rendered as text, so
  a comment can never inject markup.
-->
<template>
  <span v-if="shown" class="c-notes" :class="`c-notes--${size}`">
    <span
      v-if="herPhoto || info.note"
      class="c-notes__hers"
      :class="{ 'c-notes__hers--photo': herPhoto }"
    >
      <collector-photo
        v-if="herPhoto"
        size="her"
        framed
        :src="photoThumb(herPhoto)"
        :tag="t(tierTag(herPhoto, 'short'))"
      ></collector-photo>
      <span v-if="info.note" class="c-notes__say">
        <i18n-t
          v-if="size === 'sm'"
          keypath="collector.notes.hersInline"
          tag="span"
          scope="global"
          class="c-notes__text"
        >
          <template #note>
            <template v-for="(segment, index) in noteParts" :key="index">
              <b v-if="segment.bold">{{ segment.text }}</b>
              <template v-else>{{ segment.text }}</template>
            </template>
          </template>
        </i18n-t>
        <template v-else>
          <span class="c-notes__lab">{{ t('collector.notes.hers') }}</span>
          <span class="c-notes__text">
            <template v-for="(segment, index) in noteParts" :key="index">
              <b v-if="segment.bold">{{ segment.text }}</b>
              <template v-else>{{ segment.text }}</template>
            </template>
          </span>
        </template>
      </span>
    </span>

    <span v-if="info.brief || info.briefPhotos.length" class="c-notes__hq">
      <collector-photo
        v-for="(briefPhoto, index) in info.briefPhotos"
        :key="`${briefPhoto.url}-${index}`"
        size="hq"
        :src="photoThumb(briefPhoto)"
        :tag="t(tierTag(briefPhoto, 'short'))"
      ></collector-photo>
      <span class="c-notes__hq-body">
        <span v-if="size === 'sm' && info.brief" class="c-notes__text">
          {{ t('collector.notes.hqInline', { brief: info.brief }) }}
        </span>
        <template v-else>
          <span class="c-notes__lab">{{ t('collector.notes.hq') }}</span>
          <span v-if="info.brief" class="c-notes__text">{{ info.brief }}</span>
        </template>
      </span>
    </span>
  </span>
</template>

<script setup>
import { computed } from 'vue';
import { I18nT, useI18n } from 'vue-i18n';
import { colourWord, lineInstructions, noteSegments, photoThumb, tierTag } from '@/collector/lineView.js';
import CollectorPhoto from '@/components/collector/CollectorPhoto.vue';

const props = defineProps({
  line: { type: Object, required: true },
  /** `md` 15 px with the label over the text; `sm` 13 px with the label inline (a marked line). */
  size: { type: String, default: 'md' },
  /** `beside`: her photo sits beside her comment (a grouped member row). */
  photo: { type: String, default: 'none' },
});

const { t } = useI18n();

const info = computed(() => lineInstructions(props.line));
/** Her first photo; the rest are one tap away in the detail sheet. */
const herPhoto = computed(() => (props.photo === 'beside' ? (info.value.herPhotos[0] ?? null) : null));
const shown = computed(
  () => info.value.note != null || info.value.brief != null || info.value.briefPhotos.length > 0 || herPhoto.value != null,
);
const noteParts = computed(() => noteSegments(info.value.note, colourWord(props.line)));
</script>

<style scoped>
.c-notes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.c-notes__hers {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

/* room for the ring, which sits outside her photo (the gap under the row and
   beside her comment gives the other two sides) */
.c-notes__hers--photo {
  padding-block-start: 3px;
  padding-inline-start: 3px;
}

.c-notes__say {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1 1 0;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 12px;
  background: var(--c-lilac);
}

.c-notes__lab {
  font-size: 11px;
  font-weight: 700;
  color: var(--c-indigo);
}

.c-notes__text {
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.c-notes__say .c-notes__text {
  font-weight: 600;
  color: var(--c-ink);
}

.c-notes__say b { font-weight: 800; }

.c-notes__hq {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border: 2px solid var(--c-indigo);
  border-radius: 12px;
  background: var(--c-card);
}

/* 140 px is the least the note is read in: with several leader photos beside
   it the note wraps to its own line instead of shrinking to a sliver */
.c-notes__hq-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1 1 140px;
  min-width: 0;
}

.c-notes__hq .c-notes__text {
  font-weight: 700;
  color: var(--c-indigo);
}

.c-notes--sm .c-notes__say,
.c-notes--sm .c-notes__hq { padding: 6px 10px; }

.c-notes--sm .c-notes__text { font-size: 13px; }
</style>
